import { Box, Typography, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

// Helper function to calculate standard Y-axis configuration
function getStandardYAxisConfig(maxValue: number) {
  const standardUnits = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000];

  // Handle the case where all values are 0
  if (maxValue === 0) {
    return {
      domain: [0, 5],
      interval: 1,
      ticks: [0, 1, 2, 3, 4, 5],
    };
  }

  // Find the appropriate interval and max
  let interval = 1;
  const tickCount = 5; // Target around 5-6 ticks

  for (const unit of standardUnits) {
    const potentialTickCount = Math.ceil(maxValue / unit);
    if (potentialTickCount <= tickCount) {
      interval = unit;
      break;
    }
  }

  // Calculate the ceiling max that's a multiple of the interval
  const adjustedMax = Math.ceil(maxValue / interval) * interval;

  return {
    domain: [0, adjustedMax],
    interval: interval,
    ticks: Array.from({ length: Math.floor(adjustedMax / interval) + 1 }, (_, i) => i * interval),
  };
}

interface MinutesPlayedChartProps {
  totalMinutes: number;
  weeklyMinutes: Array<{ week_start: string; minutes: number }>;
}

export function MinutesPlayedChart({ totalMinutes, weeklyMinutes }: MinutesPlayedChartProps) {
  // Transform weekly data for the chart, taking the last 5 weeks
  const chartData = weeklyMinutes.slice(-5).map((week, _index) => ({
    date: new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    minutes: week.minutes,
    fullDate: week.week_start,
  }));

  // Format the total minutes with commas
  const formattedTotal = totalMinutes.toLocaleString();
  const formattedHours = (totalMinutes / 60).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  // Calculate Y-axis configuration with standard units
  const maxMinutes = Math.max(...chartData.map((d) => d.minutes));
  const yAxisConfig = getStandardYAxisConfig(maxMinutes);

  return (
    <Paper
      sx={{
        backgroundColor: '#1f2937',
        color: '#ffffff',
        borderRadius: 2,
        padding: 3,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: '0.875rem',
              marginBottom: '4px',
              fontWeight: 400,
            }}
          >
            Time Played
          </Typography>
          <Typography
            sx={{
              color: '#ffffff',
              fontSize: '1.5rem',
              fontWeight: 400,
            }}
          >
            {formattedTotal}
            <Typography
              component="span"
              sx={{ color: '#9ca3af', fontSize: '0.875rem', fontWeight: 400, marginLeft: '4px' }}
            >
              min
            </Typography>
          </Typography>
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: '0.875rem',
              fontWeight: 400,
              marginTop: '2px',
            }}
          >
            {formattedHours} hrs
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: '140px', flex: 1 }}>
        {chartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: -5 }}>
                <XAxis
                  dataKey="date"
                  axisLine={{ stroke: '#374151', strokeWidth: 1 }}
                  tickLine={false}
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  orientation="bottom"
                />
                <YAxis
                  axisLine={{ stroke: '#374151', strokeWidth: 1 }}
                  tickLine={false}
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  orientation="left"
                />
                <Tooltip
                  isAnimationActive={false}
                  contentStyle={{
                    backgroundColor: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '12px',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                  formatter={(value: number) => [value.toLocaleString()]}
                  labelFormatter={() => ''}
                />
                <Line
                  type="monotone"
                  dataKey="minutes"
                  stroke="#fb923c"
                  strokeWidth={2}
                  dot={{ fill: '#fb923c', strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6, stroke: '#fb923c', strokeWidth: 2, fill: '#fb923c' }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 1,
              opacity: 0.6,
            }}
          >
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #6b7280',
                  borderRadius: '4px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    width: '12px',
                    height: '8px',
                    backgroundColor: '#6b7280',
                    borderRadius: '2px',
                  },
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '0.75rem',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              No play data available
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
